import React, { useContext } from 'react';
import SbEditable, { SbEditableContent } from 'storyblok-react';
import {
  BlokComponent,
  NewStoryblokMetaData,
  StoryblokRelatedPosts,
} from '../../types/blok.model';
import defaultTheme, { darkTheme } from '../../styles/themeVariables';
import RenderAfterPageTransition from '../../old/components/RenderAfterPageTransition';
import styled, { ThemeProvider } from 'styled-components';
import { ThemeModeContextProvider } from '../../util/useThemeMode';
import { StoryblokMetaContext } from '../../old/storyblok/meta-context';
import { getBreadcrumbSchema } from '../../util/Breadcrumb/getBreadcrumbSchema';
import { useStoryblokLinks } from '../../util/useStoryblokLinks';
import { Helmet } from 'react-helmet';
import { useStaticQuery, graphql } from 'gatsby';

import { spacing } from '../../styles/newThemeVariables.styles';
import { useCaseStudyThumbnails } from '../../util/useCaseStudyThumbnails';
import { transformCategorySlugs } from '../../util/transformCollection';
import isEmpty from '../../util/isEmpty';

import CardList from '../modules/CardList';
import CardCaseStudy from '../blocks/CardCaseStudy';
import ContactFormModule from '../modules/ContactFormModule';
import { breakpointUp } from '../../styles/mediaUtils';
import Spacer from '../components/Spacer';
import PostHeaderModule from '../modules/PostHeaderModule';
import ScrollProgressBar from '../components/ScrollProgressBar';
import TableOfContents from '../components/TableOfContents';

interface CaseStudySchema extends NewStoryblokMetaData {
  title: string;
  head?: SbEditableContent[];
  body?: SbEditableContent[];
  bodyLower?: SbEditableContent[];
  bodySpacing?: string;
  bodyLowerSpacing?: string;
  lightHeader: boolean;
  related_work?: StoryblokRelatedPosts;
  hideHeroImage?: boolean;
  hideAuthor?: boolean;
  showProgress?: boolean;
  showTableOfContents?: boolean;
  tableOfContents?: {
    id: string;
    title: string;
  }[];
}

const ListContainer = styled.div`
  background: ${({ theme }) => theme.bgPrimary};
  padding-top: ${spacing.space72};
  padding-bottom: ${spacing.space72};

  @media ${breakpointUp.tablet} {
    padding-top: ${spacing.space64};
    padding-bottom: ${spacing.space64};
  }

  @media ${breakpointUp.desktop} {
    padding-top: ${spacing.space112};
    padding-bottom: ${spacing.space112};
  }
`;

const ContentWrapperLight = styled.div`
  margin-bottom: ${spacing.space40};
`;

const ContentWrapperDark = styled.div`
  padding: ${spacing.space40} 0;
  background: ${({ theme }) => theme.bgPrimary};
`;

const CaseStudyPage: BlokComponent<CaseStudySchema> = ({ blok, Dynamic }) => {
  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            siteUrl
          }
        }
      }
    `
  );

  const storyData = useContext(StoryblokMetaContext) as {
    date_published: string;
    slug: string;
    full_slug: string;
  };

  const links = useStoryblokLinks();
  const breadcrumbSchema = getBreadcrumbSchema(
    storyData,
    links,
    site.siteMetadata.siteUrl
  );

  const relatedPosts = transformCategorySlugs(
    useCaseStudyThumbnails(blok?.related_work ?? [])
  );

  return (
    <SbEditable content={blok}>
      {breadcrumbSchema && (
        <Helmet>
          <script type="application/ld+json">
            {JSON.stringify(breadcrumbSchema)}
          </script>
        </Helmet>
      )}
      <ThemeModeContextProvider themeMode={'light'}>
        <ThemeProvider theme={defaultTheme}>
          {blok.head?.map((insideBlok) =>
            insideBlok.component === 'new_CaseStudyHeaderModule' ? (
              <PostHeaderModule
                key={insideBlok._uid}
                title={insideBlok.title}
                mobile={insideBlok.bgImage?.filename}
                tablet={insideBlok.hero_img_md?.filename}
                desktop={insideBlok.hero_img_lg?.filename}
                alt={insideBlok.hero_alt}
                categories={insideBlok.categories}
                tags={insideBlok.tags}
                hideHeroImage={blok.hideHeroImage}
                hideAuthor={blok.hideAuthor}
              />
            ) : (
              <Dynamic key={insideBlok._uid} blok={insideBlok} />
            )
          )}

          <ScrollProgressBar show={blok.showProgress}>
            <TableOfContents
              show={blok.showTableOfContents}
              linksList={blok.tableOfContents}
            >
              <RenderAfterPageTransition>
                <ContentWrapperLight>
                  {blok.body?.map((insideBlok, index) => (
                    <Spacer
                      key={insideBlok._uid}
                      className={`item-${index} ${
                        insideBlok.spacing ?? blok.bodySpacing ?? 'extra-small'
                      } `}
                    >
                      <Dynamic blok={insideBlok} />
                    </Spacer>
                  ))}
                </ContentWrapperLight>
              </RenderAfterPageTransition>
            </TableOfContents>
          </ScrollProgressBar>
        </ThemeProvider>
      </ThemeModeContextProvider>

      <ThemeModeContextProvider themeMode={'dark'}>
        <ThemeProvider theme={darkTheme(defaultTheme)}>
          <RenderAfterPageTransition>
            {blok.bodyLower && blok.bodyLower?.length > 0 && (
              <ContentWrapperDark>
                {blok.bodyLower?.map((insideBlok, index) => (
                  <Spacer
                    key={insideBlok._uid}
                    className={`item-${index} ${
                      insideBlok.spacing ?? blok.bodyLowerSpacing ?? 'none'
                    } `}
                  >
                    <Dynamic blok={insideBlok} />
                  </Spacer>
                ))}
              </ContentWrapperDark>
            )}
            {relatedPosts && !isEmpty(relatedPosts) && (
              <ListContainer>
                <CardList
                  scrollMobile={true}
                  title="Related Case Studies"
                  ctaText="See All"
                  ctaLink="/work/"
                  mobile={{
                    rowGap: spacing.space64,
                    variant: 'one-column',
                  }}
                  tablet={{
                    rowGap: spacing.space64,
                    variant: 'one-column',
                  }}
                  desktop={{
                    rowGap: spacing.space112,
                    variant:
                      relatedPosts.length === 3 ? 'three-column' : 'two-column',
                  }}
                  desktopLarge={{
                    rowGap: spacing.space112,
                    variant:
                      relatedPosts.length === 3 ? 'three-column' : 'two-column',
                  }}
                >
                  {relatedPosts.map((caseStudy) => (
                    <CardCaseStudy
                      key={caseStudy.id}
                      title={caseStudy.title}
                      link={caseStudy.link}
                      clientLogo={caseStudy.logo}
                      clientLogoDark={caseStudy.logoDark}
                      clientName={caseStudy.client}
                      clientLogoAlt={caseStudy.client}
                      mobile={{
                        aspect: '3 / 4',
                        thumbnail: caseStudy.thumbnail_3x4,
                      }}
                      tablet={{
                        aspect: '4 / 3',
                        thumbnail: caseStudy.thumbnail_4x3,
                      }}
                      desktop={{
                        aspect: '3 / 4',
                        thumbnail: caseStudy.thumbnail_3x4,
                      }}
                      desktopLarge={{
                        aspect: '4 / 3',
                        thumbnail: caseStudy.thumbnail_4x3,
                      }}
                      thumbnailAlt={caseStudy.thumbnail_alt}
                    />
                  ))}
                </CardList>
              </ListContainer>
            )}
            <ContactFormModule
              form_key={'/contact - general contact'}
              tagline={'READY TO GET STARTED?'}
              title={'Transform how your most important work is delivered'}
              subtitle={
                'Meet the team, discuss your ideas with our experts and receive a proposal for your project.'
              }
            />
          </RenderAfterPageTransition>
        </ThemeProvider>
      </ThemeModeContextProvider>
    </SbEditable>
  );
};

/**
 * Handle fallback to old case study page
 */
import CaseStudyPageOld from './CaseStudyPageOld';

interface PageWithSwitcher extends CaseStudySchema {
  useLatestDesign: boolean;
}

const PageSwitcher: BlokComponent<PageWithSwitcher> = ({ blok, Dynamic }) => {
  return blok.useLatestDesign === true ? (
    <CaseStudyPage blok={blok} Dynamic={Dynamic} />
  ) : (
    <CaseStudyPageOld blok={blok} Dynamic={Dynamic} />
  );
};

export default React.memo(PageSwitcher);
